/**
 * CrmUsersCard - Gerenciamento de membros da equipe.
 * Refatorado: Tabelas extraídas para MembersTable e PendingInvitesTable.
 * Inclui modais de edição e exclusão de membros.
 */
import React, { useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { PageHeader } from '../../../components/layout/Header';
import { usePermissions } from '../../../hooks/usePermissions';
import InviteMemberModal from './InviteMemberModal';
import MembersTable, { MemberData } from './MembersTable';
import PendingInvitesTable from './PendingInvitesTable';
import DeleteMemberModal from './DeleteMemberModal';
import EditMemberModal from './EditMemberModal';
import { useOrganizationInvites } from '../hooks/useOrganizationInvites';
import { useOrganizationMembers } from '../hooks/useOrganizationMembers';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import { notify } from '../../../utils/toast';

const CrmUsersCard: React.FC = () => {
    const { user } = useAuth();
    const { canInviteMembers } = usePermissions();
    const { currentOrganization } = useOrganization();
    const {
        members,
        isLoading: isLoadingMembers,
        updateMemberRole,
        deleteMember,
        sendPasswordReset
    } = useOrganizationMembers();
    const {
        invites,
        revokeInvite,
        sendInvite,
        reshareInvite,
        regenerateInvite,
        copyInviteLink,
        isLoading: isLoadingInvites,
    } = useOrganizationInvites();

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para modais de edição e exclusão
    const [editingMember, setEditingMember] = useState<MemberData | null>(null);
    const [deletingMember, setDeletingMember] = useState<MemberData | null>(null);

    const handleInvite = (email: string, role: 'admin' | 'member' | 'viewer') => sendInvite(email, role);

    const handleEditMember = (member: MemberData) => {
        setEditingMember(member);
    };

    // Handler para salvar edição de role
    const handleSaveRole = async (newRole: 'admin' | 'member') => {
        if (!editingMember) return;
        await updateMemberRole(editingMember.id, newRole);
        setEditingMember(null);
    };

    // Handler para resetar senha
    const handleResetPassword = async (member: MemberData) => {
        if (!member.user?.email) {
            notify.error('Email do membro não encontrado');
            return;
        }
        await sendPasswordReset(member.user.email);
    };

    // Handler para iniciar exclusão (abre modal)
    const handleDeleteClick = (member: MemberData) => {
        setDeletingMember(member);
    };

    // Handler para confirmar exclusão
    const handleDeleteConfirm = async () => {
        if (!deletingMember) return;
        await deleteMember(deletingMember.id, deletingMember.user_id);
        setDeletingMember(null);
    };

    const filteredMembers = members.filter(member =>
        member.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="flex flex-col">
                <PageHeader
                    title="Membros"
                    subtitle="Gerencie os usuários da sua organização."
                    className="mb-0 gap-s3 md:mb-6 md:gap-3"
                    titleClassName="text-lg md:text-xl"
                    actionClassName="w-full md:w-auto"
                    hideBackButton
                    action={
                        <>
                            <div className="flex w-full flex-col gap-s3 md:hidden">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar membros..."
                                    className="min-h-11 w-full"
                                />
                                {canInviteMembers ? (
                                    <Button
                                        type="button"
                                        onClick={() => setIsInviteModalOpen(true)}
                                        className="min-h-11 w-full touch-manipulation"
                                    >
                                        Novo Membro
                                    </Button>
                                ) : null}
                            </div>
                            <div className="hidden md:flex md:items-center md:gap-2.5">
                                <div className="w-[250px] shrink-0">
                                    <SearchInput
                                        value={searchTerm}
                                        onChange={setSearchTerm}
                                        placeholder="Pesquisar membros..."
                                        className="h-[36px]"
                                    />
                                </div>
                                {canInviteMembers ? (
                                    <Button type="button" onClick={() => setIsInviteModalOpen(true)}>
                                        Novo Membro
                                    </Button>
                                ) : null}
                            </div>
                        </>
                    }
                />

                <section aria-label="Lista de membros" className="overflow-x-auto pt-s3 md:pt-0">
                    <MembersTable
                        members={filteredMembers}
                        isLoading={isLoadingMembers}
                        searchTerm={searchTerm}
                        currentUserRole={currentOrganization?.role || 'member'}
                        currentUserId={user?.id || ''}
                        onEdit={handleEditMember}
                        onResetPassword={handleResetPassword}
                        onDelete={handleDeleteClick}
                    />
                </section>

                {invites.length > 0 && (
                    <div className="mt-8">
                        <Card size="lg">
                            <Card.Header>
                                <Card.Title className="text-section-title">Convites Pendentes</Card.Title>
                            </Card.Header>
                            <Card.Content>
                                <PendingInvitesTable
                                    invites={invites}
                                    isLoading={isLoadingInvites}
                                    onRevoke={revokeInvite}
                                    onCopyLink={copyInviteLink}
                                    onReshare={reshareInvite}
                                    onRegenerate={regenerateInvite}
                                />
                            </Card.Content>
                        </Card>
                    </div>
                )}
            </div>

            {/* Modal de Convite */}
            <InviteMemberModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onInvite={handleInvite}
                isLoading={isLoadingInvites}
            />

            {/* Modal de Edição */}
            <EditMemberModal
                isOpen={!!editingMember}
                memberName={editingMember?.user?.full_name || ''}
                memberEmail={editingMember?.user?.email || ''}
                currentRole={editingMember?.role === 'owner' ? 'admin' : (editingMember?.role || 'member')}
                onSave={handleSaveRole}
                onClose={() => setEditingMember(null)}
            />

            {/* Modal de Exclusão */}
            <DeleteMemberModal
                isOpen={!!deletingMember}
                memberName={deletingMember?.user?.full_name || ''}
                memberEmail={deletingMember?.user?.email || ''}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeletingMember(null)}
            />
        </>
    );
};

export default CrmUsersCard;